import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { GET_PRODUCTS_MANAGE, type ManageProductsData, type ManagedProductNode } from '../../graphql/management'
import { formatPrice } from '../../lib/format'

const PAGE = 20

// priceRange 500s server-side (documented in operations.ts) — derive from variants.
function priceLabel(p: ManagedProductNode): string {
  const prices = p.productVariants.map((v) => v.price).filter((n) => n > 0)
  if (prices.length === 0) return '—'
  const min = Math.min(...prices), max = Math.max(...prices)
  return min === max ? formatPrice(min, p.currency) : `${formatPrice(min, p.currency)}–${formatPrice(max, p.currency)}`
}

export default function ProductsList() {
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const { data, loading, fetchMore } = useQuery<ManageProductsData>(GET_PRODUCTS_MANAGE, {
    variables: { first: PAGE, search: search || null }, fetchPolicy: 'cache-and-network',
  })

  const edges = data?.products.edges ?? []
  const pageInfo = data?.products.pageInfo

  function loadMore() {
    if (!pageInfo?.hasNextPage) return
    void fetchMore({
      variables: { after: pageInfo.endCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        return { products: { ...fetchMoreResult.products, edges: [...prev.products.edges, ...fetchMoreResult.products.edges] } }
      },
    })
  }

  return (
    <div className="manage-page">
      <header className="manage-header">
        <h1>Products</h1>
        <Link className="btn-primary" to="/manage/products/new">Add product</Link>
      </header>

      <form className="manage-search-row" onSubmit={(e) => { e.preventDefault(); setSearch(searchInput.trim()) }}>
        <input placeholder="Search products…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        <button className="btn-secondary" type="submit">Search</button>
        {search && <button type="button" className="btn-link" onClick={() => { setSearchInput(''); setSearch('') }}>Clear</button>}
      </form>

      {loading && edges.length === 0 ? (
        <div className="muted">Loading products…</div>
      ) : edges.length === 0 ? (
        <div className="empty-state"><h2>No products</h2><p className="muted">Nothing matches “{search}”.</p></div>
      ) : (
        <ul className="product-mgmt-list">
          {edges.map(({ node: p }) => (
            <li key={p.id} className="product-mgmt-row">
              {p.imageUrls?.[0]
                ? <img src={p.imageUrls[0]} alt="" className="product-mgmt-thumb" />
                : <div className="product-mgmt-thumb product-mgmt-thumb--empty" />}
              <div className="product-mgmt-title">
                <div>{p.title}</div>
                <div className="muted small">{p.vendor} · {priceLabel(p)} · {p.productVariants.length} variant(s) · {p.status}</div>
              </div>
              <div className="store-card-actions">
                <Link className="btn-link" to={`/manage/products/${p.id}/edit`}>Edit</Link>
                <Link className="btn-link" to={`/manage/products/${p.id}/variants`}>Variants</Link>
                <Link className="btn-link" to={`/manage/products/${p.id}/assign`}>Assign</Link>
              </div>
            </li>
          ))}
        </ul>
      )}

      {pageInfo?.hasNextPage && (
        <button className="btn-secondary load-more" onClick={loadMore} disabled={loading}>Load more</button>
      )}
    </div>
  )
}
